import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import { Input } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { uid } from "@/services/api";
import { updateClassFiche } from "@/services/ficheService";
import { formatMoney, plural } from "@/utils/format";
import type { ClassFiche, DossierItem, EnrollmentFees, FicheTranche } from "@/types/fiche";
import { currencySymbol } from "@/utils/currency";

interface Props {
  open: boolean;
  onClose: () => void;
  fiche: ClassFiche | null;
  classLabel: string;
  cycleLabel: string;
  dossiers: DossierItem[];
  fees: EnrollmentFees;
  /** Les autres classes de l'année : elles peuvent recevoir les mêmes tranches (même prix). */
  otherClasses: Array<{ fiche: ClassFiche; label: string; cycleLabel: string }>;
  onSaved: () => void;
}

type TrancheInput = Pick<FicheTranche, "label" | "amount" | "dueDate">;

/**
 * Tranches d'une autre classe alignées sur `model`, position par position : ses tranches existantes gardent
 * leur identifiant (montant, libellé et échéance mis à jour, versements conservés) ; celles en trop sont
 * retirées, sauf si des élèves les ont déjà payées (elles restent telles quelles).
 */
function alignTranches(target: ClassFiche, model: TrancheInput[]): FicheTranche[] {
  const aligned = model.map((t, i) => ({ ...target.tranches[i], ...t, id: target.tranches[i]?.id ?? uid("tr") }));
  const paidExtras = target.tranches.slice(model.length).filter((t) => t.hasPayments);
  return [...aligned, ...paidExtras];
}

/** Tranche ajoutée à l'écran, pas encore enregistrée. */
const isNewTranche = (id: string) => !/^\d+$/.test(id);

export default function ClassFicheModal({
  open,
  onClose,
  fiche,
  classLabel,
  cycleLabel,
  dossiers,
  fees,
  otherClasses,
  onSaved,
}: Props) {
  const { notify } = useToast();
  const [busy, setBusy] = useState(false);
  const [tranches, setTranches] = useState<FicheTranche[]>([]);
  const [dossierIds, setDossierIds] = useState<string[]>([]);
  /** Autres classes qui recevront les mêmes tranches. */
  const [alsoFor, setAlsoFor] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !fiche) return;
    setTranches(fiche.tranches.map((t) => ({ ...t })));
    setDossierIds([...fiche.dossierIds]);
    setAlsoFor([]);
  }, [open, fiche]);

  const toggleAlso = (classId: string) =>
    setAlsoFor((prev) => (prev.includes(classId) ? prev.filter((x) => x !== classId) : [...prev, classId]));
  const sameCycle = otherClasses.filter((o) => o.fiche.cycleId === fiche?.cycleId).map((o) => o.fiche.classId);
  // Le cycle de la classe modifiée en premier (le plus souvent au même prix).
  const cycleGroups = Array.from(new Set(otherClasses.map((o) => o.cycleLabel))).sort(
    (a, b) => Number(b === cycleLabel) - Number(a === cycleLabel),
  );

  if (!fiche) return null;

  const total = tranches.reduce((s, t) => s + (Number(t.amount) || 0), 0);

  const updateTranche = (id: string, patch: Partial<FicheTranche>) =>
    setTranches((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));

  const addTranche = () =>
    setTranches((prev) => [
      ...prev,
      { id: uid("tr"), label: `Tranche ${prev.length + 1}`, amount: 0, dueDate: "" },
    ]);

  const removeTranche = (id: string) => setTranches((prev) => prev.filter((t) => t.id !== id));

  const toggleDossier = (id: string) =>
    setDossierIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSave = async () => {
    const clean = tranches
      .filter((t) => t.label.trim() || Number(t.amount) > 0 || !isNewTranche(t.id))
      .map((t) => ({ ...t, label: t.label.trim() || "Tranche", amount: Number(t.amount) || 0 }));
    if (clean.some((t) => t.amount < 0)) {
      notify("Un montant ne peut pas être négatif.", "error");
      return;
    }
    // Les pièces « exigées partout » ne se cochent pas classe par classe.
    const ownPieces = (ids: string[]) => ids.filter((id) => !dossiers.find((d) => d.id === id)?.appliesToAllClasses);
    setBusy(true);
    try {
      await updateClassFiche(fiche.classId, { tranches: clean, dossierIds: ownPieces(dossierIds) });
    } catch (err) {
      notify(err instanceof Error ? err.message : "Enregistrement impossible.", "error");
      setBusy(false);
      return;
    }
    // Même prix pour les classes cochées : leurs pièces du dossier ne changent pas.
    const model = clean.map(({ label, amount, dueDate }) => ({ label, amount, dueDate }));
    const failed: string[] = [];
    for (const other of otherClasses.filter((o) => alsoFor.includes(o.fiche.classId))) {
      try {
        await updateClassFiche(other.fiche.classId, {
          tranches: alignTranches(other.fiche, model),
          dossierIds: ownPieces(other.fiche.dossierIds),
        });
      } catch (err) {
        failed.push(`${other.label} (${err instanceof Error ? err.message : "refusé"})`);
      }
    }
    const applied = alsoFor.length - failed.length;
    if (failed.length) notify(`Non appliqué à : ${failed.join(" ; ")}`, "error");
    notify(applied > 0 ? `Tranches enregistrées pour ${classLabel} et ${plural(applied, "autre classe")}.` : `Fiche de ${classLabel} mise à jour.`);
    setBusy(false);
    onSaved();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Fiche — ${classLabel}`}
      subtitle={`Cycle ${cycleLabel} · dossiers et tranches de cette classe`}
      icon="ri-file-text-line"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" onClick={handleSave} disabled={busy}>
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md border border-background-200 bg-background-100/60 p-3 text-xs">
          <span className="inline-flex items-center gap-2 text-foreground-700">
            <i className="ri-information-line text-sm text-secondary-700"></i>
            Frais d'inscription &amp; réinscription (uniques pour toutes les classes)
          </span>
          <span className="text-foreground-800">
            Inscription : <span className="font-semibold text-foreground-950">{formatMoney(fees.inscription)}</span>
          </span>
          <span className="text-foreground-800">
            Réinscription : <span className="font-semibold text-foreground-950">{formatMoney(fees.reinscription)}</span>
          </span>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
              Tranches de paiement
            </p>
            <div className="flex items-center gap-2">
              <Badge tone="secondary">Total annuel : {formatMoney(total)}</Badge>
              <Button size="sm" variant="outline" icon="ri-add-line" onClick={addTranche}>
                Ajouter une tranche
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            {tranches.map((t) => (
              <div
                key={t.id}
                className="grid grid-cols-1 gap-2 rounded-md border border-background-200 p-3 sm:grid-cols-[1fr_140px_1fr_auto] sm:items-end"
              >
                <Input
                  label="Libellé"
                  value={t.label}
                  onChange={(e) => updateTranche(t.id, { label: e.target.value })}
                />
                <Input
                  label={`Montant (${currencySymbol()})`}
                  type="number"
                  min={0}
                  value={String(t.amount)}
                  onChange={(e) => updateTranche(t.id, { amount: Number(e.target.value) })}
                />
                <Input
                  label="Échéance (date ou période)"
                  placeholder="ex. À l'inscription · 05/01/2027"
                  value={t.dueDate}
                  onChange={(e) => updateTranche(t.id, { dueDate: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => removeTranche(t.id)}
                  disabled={t.hasPayments}
                  aria-label="Supprimer la tranche"
                  title={t.hasPayments ? "Des versements existent : la tranche ne peut pas être retirée" : undefined}
                  className="h-9 w-9 shrink-0 flex items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-accent-50 hover:text-accent-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            ))}
            {tranches.length === 0 && (
              <p className="rounded-md border border-dashed border-background-300 py-5 text-center text-xs text-foreground-400">
                Aucune tranche. Ajoutez la première tranche de paiement.
              </p>
            )}
          </div>
        </div>

        {otherClasses.length > 0 && (
          <div className="rounded-md border border-primary-100 bg-primary-50/40 p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground-600">
                  Même prix pour d'autres classes
                </p>
                <p className="mt-0.5 text-xs text-foreground-500">
                  Les classes cochées reçoivent les mêmes tranches (libellés, montants, échéances). Leurs pièces du dossier ne
                  changent pas ; une tranche déjà payée par un élève n'est jamais retirée.
                </p>
              </div>
              <div className="flex gap-1.5">
                {sameCycle.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setAlsoFor(Array.from(new Set([...alsoFor, ...sameCycle])))}>
                    Tout le cycle
                  </Button>
                )}
                {alsoFor.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setAlsoFor([])}>
                    Aucune
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              {cycleGroups.map((group) => (
                <div key={group}>
                  <p className="mb-1 text-[11px] font-medium text-foreground-500">{group}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {otherClasses
                      .filter((o) => o.cycleLabel === group)
                      .map((o) => {
                        const on = alsoFor.includes(o.fiche.classId);
                        const current = o.fiche.tranches.reduce((s, t) => s + t.amount, 0);
                        return (
                          <button
                            key={o.fiche.classId}
                            type="button"
                            onClick={() => toggleAlso(o.fiche.classId)}
                            aria-pressed={on}
                            title={current ? `Actuellement : ${formatMoney(current)}` : "Aucune tranche pour l'instant"}
                            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                              on
                                ? "border-primary-500 bg-primary-600 text-white"
                                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                            }`}
                          >
                            {on && <i className="ri-check-line text-xs leading-none" aria-hidden="true"></i>}
                            {o.label}
                          </button>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
            {alsoFor.length > 0 && (
              <p className="mt-2 text-xs font-medium text-primary-800">
                {plural(alsoFor.length, "classe")} de plus à {formatMoney(total)} par an.
              </p>
            )}
          </div>
        )}

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">
            Dossiers à fournir pour cette classe
          </p>
          <div className="flex flex-wrap gap-2">
            {dossiers.map((d) => {
              const everywhere = d.appliesToAllClasses;
              const selected = everywhere || dossierIds.includes(d.id);
              return (
                <button
                  key={d.id}
                  type="button"
                  disabled={everywhere}
                  title={everywhere ? "Pièce exigée dans toutes les classes (voir « Gérer les dossiers »)" : undefined}
                  onClick={() => toggleDossier(d.id)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                    selected
                      ? "border-primary-500 bg-primary-600 text-white"
                      : "border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {selected && (
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-check-line text-xs leading-none"></i>
                    </span>
                  )}
                  {d.label}
                  {everywhere && <span className="opacity-80">· partout</span>}
                </button>
              );
            })}
            {dossiers.length === 0 && (
              <p className="text-xs text-foreground-400">
                Le catalogue de dossiers est vide. Ajoutez-en dans « Gérer les dossiers ».
              </p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}